import type { AgentOnboardingProgress } from "@/api/temp/AgentEstateService";

/**
 * The three steps a reader sees. The job's eight backend stages are a
 * diagnostic detail, so they are folded into these before anything is drawn;
 * the individual stages stay on the progress object for the detailed activity.
 */
export type OnboardingStepId = "importing" | "processing" | "completed";

export type OnboardingPhase = OnboardingStepId | "failed";

export type OnboardingStepState =
  | "complete"
  | "running"
  | "pending"
  | "failed"
  | "notStarted";

export interface OnboardingStep {
  id: OnboardingStepId;
  state: OnboardingStepState;
}

export interface OnboardingView {
  /** The headline state: what the job is doing, or that it failed. */
  phase: OnboardingPhase;
  steps: OnboardingStep[];
  /** Agents imported so far. Says nothing about the rest of the run. */
  importedCount: number;
}

type StepStates = readonly [
  OnboardingStepState,
  OnboardingStepState,
  OnboardingStepState,
];

const STEP_IDS: readonly OnboardingStepId[] = [
  "importing",
  "processing",
  "completed",
];

/** The state of each step, in order, for every way a job can be standing. */
const STEP_STATES = {
  importing: ["running", "pending", "pending"],
  processing: ["complete", "running", "pending"],
  completed: ["complete", "complete", "complete"],
  failedImporting: ["failed", "notStarted", "notStarted"],
  failedProcessing: ["complete", "failed", "notStarted"],
} as const satisfies Record<string, StepStates>;

const viewOf = (
  phase: OnboardingPhase,
  states: StepStates,
  importedCount: number,
): OnboardingView => ({
  phase,
  steps: STEP_IDS.map((id, index) => ({ id, state: states[index] })),
  importedCount,
});

/**
 * Collapse one progress snapshot into the three-step view.
 *
 * Importing finishing only completes the *first* step: `118 / 118 imported`
 * with seven stages still to run is "Processing", never "Completed". With no
 * snapshot yet (still loading, or unreadable) the job is shown as just
 * starting rather than guessed at.
 */
export const deriveOnboardingView = (
  progress?: AgentOnboardingProgress,
): OnboardingView => {
  const importedCount = progress?.discoveredCount ?? 0;
  const importingDone =
    progress?.stages?.find(stage => stage.id === "IMPORTING")?.status ===
    "COMPLETE";
  const failedStage = progress?.stages?.find(
    stage => stage.status === "FAILED",
  );

  if (progress?.lifecycle === "FAILED" || failedStage) {
    const failedImporting = failedStage
      ? failedStage.id === "IMPORTING"
      : !importingDone;
    return viewOf(
      "failed",
      failedImporting
        ? STEP_STATES.failedImporting
        : STEP_STATES.failedProcessing,
      importedCount,
    );
  }

  if (progress?.lifecycle === "COMPLETE")
    return viewOf("completed", STEP_STATES.completed, importedCount);

  const phase = importingDone ? "processing" : "importing";
  return viewOf(phase, STEP_STATES[phase], importedCount);
};

/**
 * When the job stopped, as an epoch time: the reported `completedAt`, or - for
 * a source that does not report one - the start plus the elapsed time.
 */
export const finishedAtOf = (progress: AgentOnboardingProgress): number =>
  progress.completedAt
    ? new Date(progress.completedAt).getTime()
    : new Date(progress.environment.startedAt).getTime() + progress.elapsedMs;
